import type {
  DismissTrackrsAttentionPayload,
  TrackrsAttentionDto,
  TrackrsConflictItem,
} from '@mesterplan/api-client';

/**
 * Pure helpers behind the "Vi har brug for din hjælp her" page, its nav badge and
 * its first-visit modal. No React and no API client here, so the optimistic
 * cache edits (what disappears, what the count becomes) and the per-user "seen"
 * storage are unit-testable in isolation — the same split as
 * `integrationConnections.utils.ts`.
 */

export const TRACKRS_ATTENTION_PATH = '/integrations/trackrs/attention';

/** A row kind that can be dismissed with "Ignorér" — everything but the whole page. */
export type DismissableAttentionKind = Exclude<DismissTrackrsAttentionPayload['kind'], 'whole_page'>;

/** Every row kind the page renders, including the match suggestions (which have no dismiss). */
export type AttentionRowKind = DismissableAttentionKind | 'same_project';

export const EMPTY_ATTENTION: TrackrsAttentionDto = {
  openCount: 0,
  remoteUnavailable: false,
  snoozedUntil: null,
  sameProject: [],
  missingCustomer: [],
  missingCaseNumber: [],
  missingStartDate: [],
  missingBudget: [],
  conflicts: [],
  remoteDeleted: [],
  autoLinked: [],
};

/**
 * The sections in the order the page shows them. `missingBudget` is informational
 * ("valgfrit") and is the one section that never adds to `openCount`.
 */
export const ATTENTION_SECTION_ORDER = [
  'sameProject',
  'missingCustomer',
  'missingCaseNumber',
  'missingStartDate',
  'missingBudget',
  'conflicts',
  'remoteDeleted',
  'autoLinked',
] as const;

export type AttentionSectionKey = (typeof ATTENTION_SECTION_ORDER)[number];

const KIND_TO_SECTION: Record<AttentionRowKind, AttentionSectionKey> = {
  same_project: 'sameProject',
  missing_customer: 'missingCustomer',
  missing_case_number: 'missingCaseNumber',
  missing_start_date: 'missingStartDate',
  missing_budget: 'missingBudget',
  conflict: 'conflicts',
  remote_deleted: 'remoteDeleted',
  auto_linked: 'autoLinked',
};

/** The sections that have at least one row, in display order. */
export function nonEmptySections(dto: TrackrsAttentionDto): AttentionSectionKey[] {
  return ATTENTION_SECTION_ORDER.filter((key) => dto[key].length > 0);
}

export function hasAnyRows(dto: TrackrsAttentionDto): boolean {
  return nonEmptySections(dto).length > 0;
}

/**
 * Whether a row is part of the server's `openCount`. Mirrors
 * `GetTrackrsAttentionQuery`: budgets are optional and a deferred match
 * suggestion ("Senere") is not open any more, so neither is counted.
 */
export function countsTowardOpen(dto: TrackrsAttentionDto, kind: AttentionRowKind, subjectId: string): boolean {
  if (kind === 'missing_budget') return false;
  if (kind === 'same_project') {
    const item = dto.sameProject.find((s) => s.subjectId === subjectId);
    return item?.state === 'open';
  }
  return dto[KIND_TO_SECTION[kind]].some((row) => row.subjectId === subjectId);
}

/**
 * The optimistic result of any action that makes a row go away: dismiss, "Samme
 * sag" / "To forskellige sager", fortryd, arkivér, or completing the field the
 * row is about. Returns a new object; the input is never mutated.
 */
export function removeAttentionRow(
  dto: TrackrsAttentionDto,
  kind: AttentionRowKind,
  subjectId: string,
): TrackrsAttentionDto {
  const section = KIND_TO_SECTION[kind];
  const counted = countsTowardOpen(dto, kind, subjectId);
  const rows = dto[section] as ReadonlyArray<{ subjectId: string }>;
  if (!rows.some((row) => row.subjectId === subjectId)) return dto;
  return {
    ...dto,
    openCount: Math.max(0, dto.openCount - (counted ? 1 : 0)),
    [section]: rows.filter((row) => row.subjectId !== subjectId),
  };
}

/** "Senere": the suggestion stays on the page, but drops out of the open count. */
export function deferAttentionRow(dto: TrackrsAttentionDto, subjectId: string): TrackrsAttentionDto {
  const item = dto.sameProject.find((s) => s.subjectId === subjectId);
  if (!item || item.state === 'deferred') return dto;
  return {
    ...dto,
    openCount: Math.max(0, dto.openCount - 1),
    sameProject: dto.sameProject.map((s) => (s.subjectId === subjectId ? { ...s, state: 'deferred' } : s)),
  };
}

/** "Opret resten som nye sager": every undecided suggestion (open or deferred) is resolved. */
export function clearSameProjectRows(dto: TrackrsAttentionDto): TrackrsAttentionDto {
  const open = dto.sameProject.filter((s) => s.state === 'open').length;
  return { ...dto, openCount: Math.max(0, dto.openCount - open), sameProject: [] };
}

/** "Ikke nu": the whole page is empty until the snooze runs out. */
export function snoozeAttention(dto: TrackrsAttentionDto, until: Date): TrackrsAttentionDto {
  return { ...EMPTY_ATTENTION, remoteUnavailable: dto.remoteUnavailable, snoozedUntil: until.toISOString() };
}

export const SNOOZE_DAYS = 7;

export function snoozeUntil(from: Date, days = SNOOZE_DAYS): Date {
  const until = new Date(from);
  until.setDate(until.getDate() + days);
  return until;
}

// --- Copy ---------------------------------------------------------------------------------

export const PRODUCT_LABEL = { mesterplan: 'Mesterplan', trackrs: 'Trackrs' } as const;

const CONFLICT_FIELD_LABEL: Record<TrackrsConflictItem['field'], string> = {
  title: 'Titel',
  siteAddress: 'Adresse',
  endDate: 'Slutdato',
  closed: 'Status',
};

export function formatDanishDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A merge-conflict value as the page shows it; booleans and dates get words, not raw strings. */
export function describeConflictValue(field: TrackrsConflictItem['field'], value: string | null): string {
  if (value === null || value.trim() === '') return 'tomt';
  if (field === 'closed') {
    const lowered = value.trim().toLowerCase();
    if (lowered === 'true' || lowered === '1') return 'lukket';
    if (lowered === 'false' || lowered === '0') return 'åben';
    return value;
  }
  if (field === 'endDate') return formatDanishDate(value) ?? value;
  return `“${value}”`;
}

export interface ConflictCopy {
  /** e.g. `Titel var forskellig — vi beholdt “Skovvej 12, tag” (fra Trackrs)`. */
  headline: string;
  /** e.g. `Mistet: “Skovvej 12” (fra Mesterplan)`. */
  detail: string;
}

export function describeConflict(item: TrackrsConflictItem): ConflictCopy {
  const field = CONFLICT_FIELD_LABEL[item.field];
  return {
    headline: `${field} var forskellig — vi beholdt ${describeConflictValue(item.field, item.keptValue)} (fra ${PRODUCT_LABEL[item.keptFrom]})`,
    detail: `Mistet: ${describeConflictValue(item.field, item.lostValue)} (fra ${PRODUCT_LABEL[item.lostFrom]})`,
  };
}

/** Danish copy for the actions' failure statuses. 404 is the backend feature flag. */
export function describeAttentionFailure(status: number): string {
  switch (status) {
    case 403:
      return 'Du har ikke rettighed til at ændre dette. Bed en administrator.';
    case 404:
      return 'Produktforbindelser er ikke slået til i Mesterplan endnu. Kontakt support.';
    case 409:
      return 'Det er allerede afgjort et andet sted. Listen er opdateret.';
    case 502:
      return 'Trackrs kunne ikke kontaktes lige nu. Prøv igen om et øjeblik.';
    default:
      return 'Det lykkedes ikke. Prøv igen.';
  }
}

// --- First-visit modal: per-user "seen" flag -------------------------------------------------

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** `localStorage`, or null where merely touching it throws (sandboxed frames, some private modes). */
export function safeLocalStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const SEEN_PREFIX = 'mp.trackrsAttention.introSeen';

/** Per-user so a shared browser does not hide the intro from the next admin who logs in. */
export function attentionIntroSeenKey(userId: string): string {
  return `${SEEN_PREFIX}.${userId}`;
}

export function hasSeenAttentionIntro(storage: StorageLike | null, userId: string | null): boolean {
  if (!storage || !userId) return false;
  try {
    return storage.getItem(attentionIntroSeenKey(userId)) !== null;
  } catch {
    // Storage unavailable (private mode / quota): treat as unseen — one extra modal beats none.
    return false;
  }
}

export function markAttentionIntroSeen(storage: StorageLike | null, userId: string | null): void {
  if (!storage || !userId) return;
  try {
    storage.setItem(attentionIntroSeenKey(userId), new Date().toISOString());
  } catch {
    /* storage unavailable — the modal simply shows again next time */
  }
}
